import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  Divider
} from "@/components/ds";

interface Props {
  selectedConfigId: number;
}

interface CtiUrls {
  id: number;
  ctiUrlToken: string | null;
  dialUrlTemplate: string | null;
  screenPopUrl: string | null;
}

function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard permission denied — the field is still selectable/copyable manually */
    }
  }, [value]);
  return (
    <div className="w-full">
      <label className="block text-xs font-medium text-zinc-500 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={label}
          className="flex-1 min-w-0 text-xs font-mono px-2 py-1.5 rounded border border-zinc-200 bg-zinc-50 text-zinc-700 truncate"
        />
        <SecondaryButton onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </SecondaryButton>
      </div>
    </div>
  );
}

/**
 * Backs NetSuite's native "Telephony Integration" SuiteCloud feature — a NetSuite admin pastes the
 * dial URL into Home > Set Preferences > Telephony > "CTI URL". This panel is the only UI that can
 * generate/rotate the per-site NetSuiteConfig.CtiUrlToken these URLs are built from
 * (server/Velaro.Messaging/Controllers/CRMs/NetsuiteIntegrationsController.cs GenerateCtiTokenAsync).
 */
export default function NetSuiteCtiSetup({ selectedConfigId }: Props) {
  const api = useApi();
  const [urls, setUrls] = useState<CtiUrls | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedConfigId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.messaging.get(
        `NetSuite/Integrations/${selectedConfigId}/CtiUrls`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setUrls(await resp.json());
    } catch (err) {
      setError(
        `Failed to load CTI setup: ${err instanceof Error ? err.message : String(err)}`
      );
      setUrls(null);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, selectedConfigId]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await api.messaging.post(
        `NetSuite/Integrations/${selectedConfigId}/GenerateCtiToken`,
        {}
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setUrls(await resp.json());
    } catch (err) {
      setError(
        `Failed to generate CTI token: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setGenerating(false);
    }
  };

  if (!selectedConfigId) return null;

  return (
    <SectionCard
      title="Click-to-Dial (CTI)"
      action={
        <PrimaryButton onClick={generate} disabled={generating || loading}>
          {generating
            ? "Generating…"
            : urls?.ctiUrlToken
              ? "Regenerate URL"
              : "Generate URL"}
        </PrimaryButton>
      }
    >
      <p className="text-sm text-zinc-500 mb-3">
        Powers NetSuite's built-in Telephony Integration — clicking a phone
        number on any NetSuite record dials out through your connected voice
        provider. Paste the dial URL below into{" "}
        <strong>Home &gt; Set Preferences &gt; Telephony &gt; CTI URL</strong>{" "}
        in NetSuite.
      </p>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : !urls?.ctiUrlToken ? (
        <p className="text-sm text-zinc-400">
          No CTI URL generated yet. Click "Generate URL" to create one.
        </p>
      ) : (
        <div className="space-y-3">
          <CopyField
            label="CTI URL (paste into NetSuite Telephony preferences)"
            value={urls.dialUrlTemplate!}
          />
          <Divider />
          <CopyField
            label="Screen-pop URL (optional)"
            value={urls.screenPopUrl!}
          />
          <p className="text-xs text-zinc-400">
            Screen-pop is optional — only needed if your NetSuite
            workflow/customization navigates here on an inbound call. Not
            confirmed as automatic by NetSuite's own documentation.
          </p>
          <p className="text-xs text-zinc-400">
            Regenerating invalidates the URL above immediately — update
            NetSuite's Telephony preferences with the new URL, or click-to-dial
            will start returning 401s.
          </p>
        </div>
      )}
    </SectionCard>
  );
}
